import {
  CustomConditionModel,
  FormCondition,
  Condition,
  ConditionType,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client';
import {
  AgxButton,
  AgxCollapsible,
  AgxOnOrDaysFrom,
  AgxSpacer,
  AgxTextArea,
  AgxToggleSwitch,
  ContractDateType,
  Icon_TrashOutline,
} from '@urbanx/agx.ui-components2';
import { useCallback, useEffect, useState } from 'react';

interface CustomSpecialConditionProps {
  specialConditions: SpecialConditionData[];
  customCondition: CustomConditionModel;
  onChange: (_customConditionModel: CustomConditionModel) => void;
  onDelete: (_clauseId: string) => void;
  onIsValidChange: (_clauseId: string, _isValid: boolean) => void;
  triggerValidation?: boolean;
  clausePosition: string;
  campaignId: string;
  hideDeleteButton?: boolean;
}

const conditionsToInclude: Condition[] = [
  Condition.OnDate,
  Condition.NdaysFromDate,
  Condition.OnSpecificDate,
  Condition.AsPerContract,
];

const CustomSpecialCondition = ({
  specialConditions,
  customCondition,
  onChange,
  clausePosition,
  campaignId,
  onDelete,
  onIsValidChange,
  triggerValidation = false,
  hideDeleteButton = false,
}: CustomSpecialConditionProps) => {
  const onChangeClauseText = useCallback(
    (newText: string) => {
      const updatedCustomCondition: CustomConditionModel = {
        ...customCondition,
        textInput: newText,
      };

      onChange(updatedCustomCondition);
    },
    [customCondition, onChange]
  );

  const [dueDateSet, updateDueDateSet] = useState<boolean>(
    () => !!customCondition.overriddenDueDate
  );

  const onNoDueDateToggle = useCallback(
    (checked: boolean) => {
      updateDueDateSet(checked);

      const updatedCustomCondition: CustomConditionModel = {
        ...customCondition,
      };

      onChange(updatedCustomCondition);
    },
    [customCondition, onChange]
  );

  const overriddenDateChange = useCallback(
    (newCondition: FormCondition) => {
      const updatedCustomCondition: CustomConditionModel = {
        ...customCondition,
        overriddenDueDate: {
          ...newCondition,
          conditionType:
            newCondition.conditionType ??
            (newCondition.condition === Condition.NdaysFromDate ||
            newCondition.condition === Condition.AsPerContract ||
            newCondition.condition === Condition.OnDate
              ? ConditionType.Contract
              : undefined),
        },
      };

      onChange(updatedCustomCondition);
    },
    [customCondition, onChange]
  );

  const onSubClauseDelete = useCallback(() => {
    if (!customCondition.clauseId) return;

    onDelete(customCondition.clauseId);
  }, [onDelete, customCondition.clauseId]);

  const [isOnOrDaysFromValid, setIsOnOrDaysFromValid] = useState<boolean>(false);

  useEffect(() => {
    setIsOnOrDaysFromValid(false);
  }, [dueDateSet]);

  useEffect(() => {
    if (!customCondition.clauseId) return;

    const hasClauseText = !!customCondition.textInput?.trim();
    const hasValidDueDate = !dueDateSet || isOnOrDaysFromValid;

    onIsValidChange(customCondition.clauseId, hasClauseText && hasValidDueDate);
  }, [
    customCondition.clauseId,
    customCondition.textInput,
    dueDateSet,
    isOnOrDaysFromValid,
    onIsValidChange,
  ]);

  return (
    <AgxCollapsible
      variant={'withDivider'}
      content={
        <AgxSpacer fill gap="spacing-xl" padding="spacing-2xl">
          <AgxTextArea
            id={`custom-condition-clause-${customCondition.clauseId}`}
            hideOptionalText
            required
            label="Details of Special Condition"
            defaultValue={customCondition.textInput || ''}
            onInputValueChange={({ value }) => onChangeClauseText(value)}
            rows={6}
            maxLength={1100}
            stretch
            error={
              !customCondition.textInput?.trim() && triggerValidation
                ? 'Special Condition Details are required'
                : undefined
            }
          />
          <AgxToggleSwitch
            id={`custom-condition-toggle-${customCondition.clauseId}`}
            checked={dueDateSet}
            onChange={onNoDueDateToggle}
            offLabel="No Due Date"
            onLabel="Due Date Set"
          />
          {dueDateSet && (
            <AgxOnOrDaysFrom
              id={`custom-condition-daysFrom-${customCondition.clauseId}`}
              condition={customCondition.overriddenDueDate ?? {}}
              conditionsToIncludeInOn={conditionsToInclude}
              conditionsToIncludeInDaysFrom={conditionsToInclude}
              specialConditions={specialConditions}
              campaignId={campaignId}
              onChange={overriddenDateChange}
              validate={triggerValidation}
              contractDateType={ContractDateType.ContractDate}
              includeContractDateTypeInOnOptions={false}
              onIsValidChange={setIsOnOrDaysFromValid}
            />
          )}
        </AgxSpacer>
      }
      open={true}
      enableToggle
      title={`Clause ${clausePosition}`}
      headerPadding={{
        top: 'spacing-xl',
        bottom: 'spacing-xl',
        left: 'spacing-2xl',
        right: 'spacing-2xl',
      }}
      iconPosition="left"
      actionContent={
        !hideDeleteButton ? (
          <AgxButton
            id={`custom-condition-action-${customCondition.clauseId}`}
            text={''}
            variant={'image'}
            onClick={onSubClauseDelete}
            leftIcon={<Icon_TrashOutline height={18} width={18} />}
          />
        ) : undefined
      }
      isContentFit
    />
  );
};

export default CustomSpecialCondition;
